import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, Clock, User, Receipt, X,
  MessageSquare, Wallet, Send, ChevronRight, FileText, Download,
} from "lucide-react";

const STATUS_META = {
  submitted:          { label: "În așteptare Manager",  fg: "#a16207", bg: "#fef9c3" },
  info_requested:     { label: "Info suplimentare",     fg: "#c2410c", bg: "#ffedd5" },
  manager_approved:   { label: "În verificare Finance", fg: "#1d4ed8", bg: "#dbeafe" },
  finance_verified:   { label: "Verificat, așteaptă plată", fg: "#1d4ed8", bg: "#dbeafe" },
  approved_for_payment: { label: "Aprobat pentru plată", fg: "#166534", bg: "#dcfce7" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, fg: "#374151", bg: "#f4f4f5" };
  return (
    <span className="inline-flex items-center px-2.5 h-6 text-[11px] font-medium rounded-md"
      style={{ background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

function fmtMoney(v, cur = "RON") {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}
const AVATAR_COLORS = [
  { bg: "#EEF2F7", fg: "#3d68b1" }, { bg: "#F4EFE9", fg: "#8B6E4A" },
  { bg: "#EEF3EF", fg: "#3F6B4E" }, { bg: "#F5EEF0", fg: "#A0546C" },
  { bg: "#F0EEF5", fg: "#5B4E8A" }, { bg: "#F3F1EA", fg: "#7A6B34" },
];
function initials(n = "") { return n.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase() || "").join(""); }
function colorFor(s = "") { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff; return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]; }
function Avatar({ name, size = 40 }) {
  const c = colorFor(name || "?");
  return (
    <div className="flex items-center justify-center font-medium shrink-0 rounded-lg"
      style={{ width: size, height: size, background: c.bg, color: c.fg, fontSize: size * 0.35 }}>
      {initials(name) || "?"}
    </div>
  );
}


export default function ExpensesApprovals() {
  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState(null); // report id
  const [me, setMe] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/auth/me"); setMe(data); }
      catch (e) { /* ignore */ }
    })();
  }, []);

  const isFinance = me?.role === "admin" || (me?.permissions || []).includes("finance.manage");
  const isHR = me?.role === "admin" || (me?.permissions || []).includes("hr.manage");
  const canHistory = isFinance || isHR;

  const TABS = [
    { id: "pending", label: "De aprobat" },
    ...(canHistory ? [{ id: "history", label: "Istoric deconturi" }] : []),
  ];

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1200px] mx-auto">
        <header className="pb-6 pt-2">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
            Aprobări
          </div>
          <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            Deconturi
          </h1>
        </header>

        <div className="flex gap-1 border-b border-neutral-200 mb-8">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`exp-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${
                tab === t.id ? "border-brand text-brand" : "border-transparent text-ink-muted hover:text-ink"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "pending" && (
          <PendingView isFinance={isFinance} onSelect={setSelected} reloadKey={reloadKey} />
        )}
        {tab === "history" && canHistory && (
          <HistoryView onSelect={setSelected} reloadKey={reloadKey} />
        )}
      </div>

      <ApprovalSheet
        open={!!selected} reportId={selected}
        onClose={() => setSelected(null)}
        onDone={() => { setSelected(null); setReloadKey(k => k + 1); }}
        isFinance={isFinance}
      />
    </PortalLayout>
  );
}


function PendingView({ isFinance, onSelect, reloadKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPersons, setOpenPersons] = useState({});
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthTouched, setMonthTouched] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses/reports/pending");
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [reloadKey]);

  // Implicit: luna celui mai recent decont (după data trimiterii/creării)
  useEffect(() => {
    if (monthTouched || !items.length) return;
    const months = items
      .map(r => (r.submitted_at || r.created_at || "").slice(0, 7))
      .filter(Boolean);
    if (months.length) setMonth(months.sort().reverse()[0]);
  }, [items, monthTouched]);

  const reportMonth = (r) => (r.submitted_at || r.created_at || "").slice(0, 7);

  const byPerson = useMemo(() => {
    const m = {};
    for (const r of items) {
      if (month && reportMonth(r) !== month) continue;
      const uid = r.employee?.id || r.user_id;
      if (!m[uid]) m[uid] = { uid, employee: r.employee, reports: [] };
      m[uid].reports.push(r);
    }
    return Object.values(m).sort((a, b) => (a.employee?.name || "").localeCompare(b.employee?.name || ""));
  }, [items, month]);

  return (
    <>
      <p className="text-[15px] leading-relaxed text-ink-muted max-w-xl mb-6">
        {isFinance
          ? "Deconturile sunt centralizate per persoană. Aprobă-le, verifică-le și descarcă decontul lunar consolidat."
          : "Deconturile echipei tale, centralizate per persoană."}
      </p>

      {/* Selector lună — filtrează deconturile și decontul lunar consolidat */}
      <div className="flex items-center gap-3 pb-6 border-b border-neutral-200 mb-8 flex-wrap">
        <span className="text-[13px] text-ink-muted">Luna:</span>
        <input type="month" value={month} onChange={e => { setMonth(e.target.value); setMonthTouched(true); }}
          data-testid="monthly-decont-month"
          className="border border-neutral-300 px-3 py-2 text-sm rounded-md" />
      </div>

      {loading ? (
        <div className="py-24 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
        </div>
      ) : byPerson.length === 0 ? (
        <div className="py-24 text-center border-t border-neutral-200" data-testid="pending-empty">
          <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-3" />
          <div className="text-[15px] text-ink-muted">Nu ai deconturi de aprobat în luna selectată.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {byPerson.map(p => {
            const isOpen = !!openPersons[p.uid];
            return (
            <section key={p.uid} className="border border-neutral-200 rounded-xl overflow-hidden" data-testid={`person-group-${p.uid}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 bg-neutral-50/60">
                <button onClick={() => setOpenPersons(s => ({ ...s, [p.uid]: !s[p.uid] }))}
                  data-testid={`person-toggle-${p.uid}`}
                  className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 text-left">
                  <ChevronRight className={`h-5 w-5 text-neutral-400 transition-transform shrink-0 ${isOpen ? "rotate-90" : ""}`} />
                  <Avatar name={p.employee?.name} size={44} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-medium text-ink truncate">{p.employee?.name}</div>
                    <div className="text-[13px] text-ink-muted">{p.reports.length} decont{p.reports.length !== 1 ? "uri" : ""} de procesat</div>
                  </div>
                </button>
                <a
                  href={`${backend}/api/expenses/export-decont-monthly?user_id=${p.uid}&month=${month}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`monthly-decont-${p.uid}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-neutral-300 hover:border-ink hover:bg-white text-[13px] font-medium text-ink transition-colors shrink-0">
                  <Download className="h-4 w-4" /> Decont lunar {month} (Excel)
                </a>
              </div>
              {isOpen && (
                <div className="px-2 border-t border-neutral-200">
                  {p.reports.map(r => (
                    <ApprovalRow key={r.id} report={r} onClick={() => onSelect(r.id)} />
                  ))}
                </div>
              )}
            </section>
            );
          })}
        </div>
      )}
    </>
  );
}


function HistoryView({ onSelect, reloadKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [openPersons, setOpenPersons] = useState({});
  const backend = process.env.REACT_APP_BACKEND_URL;

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses/reports/all");
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [reloadKey]);

  const reportMonth = (r) => (r.submitted_at || r.created_at || "").slice(0, 7);

  const months = useMemo(() => {
    const s = new Set(items.map(reportMonth).filter(Boolean));
    return [...s].sort().reverse();
  }, [items]);

  useEffect(() => {
    if (!month && months.length) setMonth(months[0]);
  }, [months, month]);

  const byPerson = useMemo(() => {
    const q = search.trim().toLowerCase();
    const m = {};
    for (const r of items) {
      if (month && reportMonth(r) !== month) continue;
      const name = (r.employee?.name || "").toLowerCase();
      if (q && !name.includes(q)) continue;
      const uid = r.employee?.id || r.user_id;
      if (!m[uid]) m[uid] = { uid, employee: r.employee, reports: [], total: 0 };
      m[uid].reports.push(r);
      m[uid].total += r.totals?.requested_ron || 0;
    }
    return Object.values(m).sort((a, b) => (a.employee?.name || "").localeCompare(b.employee?.name || ""));
  }, [items, month, search]);

  const monthLabel = (ym) => {
    if (!ym) return "";
    const [y, mm] = ym.split("-");
    const names = ["ian.","feb.","mar.","apr.","mai","iun.","iul.","aug.","sep.","oct.","nov.","dec."];
    return `${names[parseInt(mm, 10) - 1]} ${y}`;
  };

  return (
    <>
      <p className="text-[15px] leading-relaxed text-ink-muted max-w-xl mb-6">
        Istoricul deconturilor pe fiecare lună și angajat, indiferent de status. Descarcă decontul lunar consolidat pentru fiecare persoană.
      </p>

      <div className="flex items-center gap-3 pb-6 border-b border-neutral-200 mb-8 flex-wrap">
        <span className="text-[13px] text-ink-muted">Luna:</span>
        <select value={month} onChange={e => setMonth(e.target.value)}
          data-testid="history-month"
          className="border border-neutral-300 px-3 py-2 text-sm rounded-md bg-white">
          {months.length === 0 && <option value="">—</option>}
          {months.map(ym => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
        </select>
        <div className="flex items-center gap-2 ml-auto h-10 px-3 rounded-md border border-neutral-300 bg-white">
          <User className="h-4 w-4 text-neutral-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            data-testid="history-search" placeholder="Caută angajat..."
            style={{ boxShadow: "none" }}
            className="bg-transparent outline-none border-0 text-sm placeholder:text-neutral-400" />
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
        </div>
      ) : byPerson.length === 0 ? (
        <div className="py-24 text-center border-t border-neutral-200 text-[15px] text-ink-muted" data-testid="history-empty">
          Niciun decont în luna selectată.
        </div>
      ) : (
        <div className="space-y-4" data-testid="history-list">
          {byPerson.map(p => {
            const isOpen = !!openPersons[p.uid];
            return (
            <section key={p.uid} className="border border-neutral-200 rounded-xl overflow-hidden" data-testid={`history-person-${p.uid}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 bg-neutral-50/60">
                <button onClick={() => setOpenPersons(s => ({ ...s, [p.uid]: !s[p.uid] }))}
                  data-testid={`history-toggle-${p.uid}`}
                  className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 text-left">
                  <ChevronRight className={`h-5 w-5 text-neutral-400 transition-transform shrink-0 ${isOpen ? "rotate-90" : ""}`} />
                  <Avatar name={p.employee?.name} size={44} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-medium text-ink truncate">{p.employee?.name}</div>
                    <div className="text-[13px] text-ink-muted">
                      {p.reports.length} decont{p.reports.length !== 1 ? "uri" : ""} · {fmtMoney(p.total)}
                    </div>
                  </div>
                </button>
                <a
                  href={`${backend}/api/expenses/export-decont-monthly?user_id=${p.uid}&month=${month}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`history-decont-${p.uid}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-neutral-300 hover:border-ink hover:bg-white text-[13px] font-medium text-ink transition-colors shrink-0">
                  <Download className="h-4 w-4" /> Decont lunar {monthLabel(month)} (Excel)
                </a>
              </div>
              {isOpen && (
                <div className="px-2 border-t border-neutral-200">
                  {p.reports.map(r => (
                    <ApprovalRow key={r.id} report={r} onClick={() => onSelect(r.id)} />
                  ))}
                </div>
              )}
            </section>
            );
          })}
        </div>
      )}
    </>
  );
}


function Group({ title, subtitle, items, onSelect }) {
  return (
    <section>
      <div className="mb-4 border-t border-neutral-200 pt-6">
        <div className="text-[15px] font-medium text-ink">{title}</div>
        <div className="text-[13px] text-ink-muted mt-0.5">{subtitle} · {items.length} decont{items.length !== 1 ? "uri" : ""}</div>
      </div>
      <div>
        {items.map(r => (
          <ApprovalRow key={r.id} report={r} onClick={() => onSelect(r.id)} />
        ))}
      </div>
    </section>
  );
}


function ApprovalRow({ report, onClick }) {
  const t = report.totals || {};
  return (
    <button onClick={onClick}
      className="group w-full flex items-center gap-3 sm:gap-4 py-4 border-b border-neutral-100 hover:bg-neutral-50 transition-colors text-left">
      <Avatar name={report.employee?.name} size={44} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-1 flex-wrap">
          <div className="text-[15px] font-medium text-ink truncate">{report.title}</div>
          <StatusPill status={report.status} />
        </div>
        <div className="text-[13px] text-ink-muted truncate">
          {report.employee?.name} · {report.employee?.job_title} · {report.items?.length || 0} cheltuieli
        </div>
        <div className="sm:hidden mt-1 text-[13px] font-medium tabular-nums text-ink">
          {fmtMoney(t.requested_ron)} <span className="text-[11px] text-ink-muted font-normal">· {fmtDate(report.submitted_at)}</span>
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="text-[15px] font-medium tabular-nums text-ink">
          {fmtMoney(t.requested_ron)}
        </div>
        <div className="text-[12px] text-ink-muted">Trimis {fmtDate(report.submitted_at)}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-neutral-300 group-hover:text-ink shrink-0" />
    </button>
  );
}


function ApprovalSheet({ open, onClose, reportId, onDone, isFinance }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [dialog, setDialog] = useState(null); // {action, defaultComment?, needDate?}
  const [comment, setComment] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [approvedItems, setApprovedItems] = useState({});
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!open || !reportId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/expenses/reports/${reportId}`);
        setReport(data);
        const init = {};
        for (const it of data.items || []) init[it.id] = it.approved_amount ?? it.amount;
        setApprovedItems(init);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [open, reportId]);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  async function runAction(url, msg, payload = {}) {
    setBusy(url);
    try {
      await api.post(url, payload);
      toast.success(msg);
      setDialog(null); setComment("");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(null); }
  }

  function openDialog(cfg) {
    setComment(cfg.defaultComment || "");
    setDialog(cfg);
  }

  if (!open) return null;
  const t = report?.totals || {};
  const canManagerAct = report && ["submitted", "info_requested"].includes(report.status);
  const canFinanceVerify = report && report.status === "manager_approved";
  const canFinanceApprovePay = report && ["manager_approved", "finance_verified"].includes(report.status);
  const canFinanceMarkPaid = report && report.status === "approved_for_payment";

  return (
    <>
      <div onClick={onClose}
        className={`fixed inset-0 bg-ink/20 transition-opacity duration-300 z-40 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`} />
      <aside className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-[900px] bg-white border-l border-neutral-200 flex flex-col transition-transform duration-300 ease-out ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>
        {loading || !report ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
          </div>
        ) : (
          <>
            <div className="px-8 pt-8 pb-6 border-b border-neutral-100">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <Avatar name={report.employee?.name} size={56} />
                  <div className="min-w-0">
                    <div className="text-[12px] font-display font-medium text-ink-muted mb-1 flex items-center gap-3">
                      {report.employee?.name} · {report.employee?.job_title}
                    </div>
                    <div className="text-[22px] font-display font-semibold tracking-[-0.01em] text-ink truncate">
                      {report.title}
                    </div>
                    <div className="mt-2"><StatusPill status={report.status} /></div>
                  </div>
                </div>
                <button onClick={onClose}
                  className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-5">
                <a
                  href={`${backend}/api/expenses/reports/${report.id}/export-decont`}
                  target="_blank" rel="noreferrer"
                  data-testid="download-decont-excel"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-neutral-300 hover:border-ink hover:bg-neutral-50 text-[13px] font-medium text-ink transition-colors">
                  <Download className="h-4 w-4" /> Descarcă decont (Excel)
                </a>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-6 mt-6">
                <MiniStat label="Solicitat" value={fmtMoney(t.requested_ron)} />
                <MiniStat label="Aprobat" value={fmtMoney(t.approved_ron)} />
                <MiniStat label="Cheltuieli" value={report.items?.length || 0} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8 space-y-10">
              {/* Cheltuieli — cu opțiunea de a modifica approved_amount */}
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-4">
                  Cheltuieli & sume aprobate
                </div>
                <div className="space-y-3">
                  {report.items.map(it => (
                    <div key={it.id} className="border border-neutral-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex items-center gap-2.5">
                          <span className="text-lg">{it.category?.icon}</span>
                          <div>
                            <div className="text-[14px] font-medium">{it.category?.name}</div>
                            <div className="text-[12px] text-ink-muted">
                              {fmtDate(it.expense_date)} · {it.vendor || "—"} · {it.description || "—"}
                            </div>
                          </div>
                        </div>
                        <div className="text-[15px] font-medium tabular-nums shrink-0">
                          {fmtMoney(it.amount, it.currency)}
                        </div>
                      </div>
                      {it.project && (
                        <div className="text-[12px] text-ink-muted mt-1">
                          {it.project.code} — {it.project.name}
                          {it.phase && ` · ${it.phase}`}
                        </div>
                      )}
                      {canManagerAct && (
                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                          <label className="text-[12px] font-display font-medium text-ink-muted">
                            Aprobă suma:
                          </label>
                          <input type="number" step="0.01"
                            value={approvedItems[it.id] ?? it.amount}
                            onChange={e => setApprovedItems({ ...approvedItems, [it.id]: parseFloat(e.target.value) || 0 })}
                            className="w-32 border-b border-neutral-300 py-1 text-[14px] tabular-nums focus:border-ink outline-none bg-transparent rounded-none"
                            style={{ borderRadius: 0 }} />
                          <button
                            onClick={() => setApprovedItems({ ...approvedItems, [it.id]: 0 })}
                            className="text-[12px] text-red-600 hover:underline">
                            Respinge linie
                          </button>
                          <button
                            onClick={() => setApprovedItems({ ...approvedItems, [it.id]: it.amount })}
                            className="text-[12px] text-ink-muted hover:text-ink">
                            Resetează
                          </button>
                        </div>
                      )}
                      {it.documents?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {it.documents.map(d => (
                            <a key={d.id}
                              href={`${backend}/api/expenses/reports/${report.id}/items/${it.id}/attachments/${d.id}/download`}
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-md text-[12px]">
                              <FileText className="h-3 w-3" />{d.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Istoric */}
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-4">
                  Istoric
                </div>
                <div className="space-y-3">
                  {(report.history || []).map((h, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="text-[11px] text-ink-muted tabular-nums min-w-[120px]">
                        {`${fmtDate(h.at)}, ${new Date(h.at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                      </div>
                      <div>
                        <div className="text-[13px] text-ink"><span className="font-medium">{h.by_name}</span> · {h.action}</div>
                        {h.comment && <div className="text-[12px] text-ink-muted mt-0.5">{h.comment}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="px-8 py-5 border-t border-neutral-100 flex items-center gap-3 flex-wrap">
              <Button variant="ghost" size="md" onClick={onClose}>Închide</Button>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                {canManagerAct && (
                  <>
                    <Button variant="secondary" size="md" icon={MessageSquare}
                      onClick={() => openDialog({ action: "request-info", label: "Cerere info suplimentare" })}>
                      Cere info
                    </Button>
                    <Button variant="danger" size="md" icon={XCircle}
                      onClick={() => openDialog({ action: "reject-manager", label: "Respinge decont", requireComment: true })}>
                      Respinge
                    </Button>
                    <Button variant="primary" size="md" icon={CheckCircle2}
                      onClick={() => openDialog({ action: "approve-manager", label: "Aprobă ca Manager" })}>
                      Aprobă
                    </Button>
                  </>
                )}
                {canFinanceVerify && (
                  <>
                    <Button variant="secondary" size="md" icon={MessageSquare}
                      onClick={() => openDialog({ action: "request-info", label: "Cere info" })}>
                      Cere info
                    </Button>
                    <Button variant="primary" size="md" icon={CheckCircle2}
                      onClick={() => openDialog({ action: "verify-finance", label: "Verificat Finance" })}>
                      Marchează verificat
                    </Button>
                  </>
                )}
                {canFinanceApprovePay && !canFinanceMarkPaid && (
                  <Button variant="primary" size="md" icon={Wallet}
                    onClick={() => openDialog({ action: "approve-payment", label: "Aprobă pentru plată" })}>
                    Aprobă pentru plată
                  </Button>
                )}
                {canFinanceMarkPaid && (
                  <Button variant="primary" size="md" icon={Send}
                    onClick={() => openDialog({ action: "mark-paid", label: "Marchează plătit", needDate: true })}>
                    Marchează plătit
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </aside>

      {/* Confirmation dialog */}
      {dialog && (
        <div className="fixed inset-0 z-[60] bg-ink/40 flex items-center justify-center p-4"
          onClick={() => setDialog(null)}>
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-md w-full p-8">
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2">
              Acțiune
            </div>
            <div className="text-[20px] font-display font-semibold text-ink mb-6">
              {dialog.label}
            </div>
            {dialog.needDate && (
              <div className="mb-5">
                <div className="text-[12px] font-medium text-ink-muted mb-2">Data plății</div>
                <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)}
                  className="w-full border-b border-neutral-200 py-2 text-[15px] outline-none focus:border-ink bg-transparent"
                  style={{ borderRadius: 0 }} />
              </div>
            )}
            <div className="mb-6">
              <div className="text-[12px] font-medium text-ink-muted mb-2">
                Comentariu {dialog.requireComment && <span className="text-ink">*</span>}
              </div>
              <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Motiv / observații"
                className="w-full border border-neutral-200 rounded-lg p-3 text-[14px] outline-none focus:border-ink resize-none" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" size="md" onClick={() => setDialog(null)}>Anulează</Button>
              <Button variant="primary" size="md"
                disabled={dialog.requireComment && !comment.trim()}
                loading={busy === `/expenses/reports/${report.id}/${dialog.action}`}
                onClick={() => runAction(
                  `/expenses/reports/${report.id}/${dialog.action}`,
                  "Acțiune executată",
                  {
                    comment,
                    payment_date: dialog.needDate ? paymentDate : undefined,
                    approved_items: (dialog.action === "approve-manager" || dialog.action === "verify-finance") ? approvedItems : undefined,
                  }
                )}>
                Confirmă
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[12px] font-display font-medium text-ink-muted mb-1">{label}</div>
      <div className="text-[16px] font-medium tabular-nums text-ink">{value}</div>
    </div>
  );
}
